import { ArrowUpDownIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

/**
 * Click-to-sort table header cell. Tri-state via the parent: click an
 * inactive column to set asc; click the active column to flip asc/desc.
 *
 * Lives in `components/` because both Beheer Kennisbank (slice 5) and
 * Beheer Sociale Kaart (slice 2a + 2026-05-01 follow-up) want the same
 * affordance with the same a11y + visual contract.
 */

export interface SortableThProps<TColumn extends string> {
  label: string;
  column: TColumn;
  sortBy: TColumn;
  sortDir: 'asc' | 'desc';
  onSort: (c: TColumn) => void;
  className?: string;
}

export function SortableTh<TColumn extends string>({
  label,
  column,
  sortBy,
  sortDir,
  onSort,
  className,
}: SortableThProps<TColumn>): JSX.Element {
  const active = sortBy === column;
  return (
    <th className={className ?? 'px-4 py-3'}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 text-left text-[11px] uppercase tracking-wider text-ink-800/60 hover:text-ink-900"
      >
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUpIcon size={11} />
          ) : (
            <ChevronDownIcon size={11} />
          )
        ) : (
          <ArrowUpDownIcon size={11} className="opacity-30" />
        )}
      </button>
    </th>
  );
}

/**
 * Generic toggler: click the active column to flip direction; click an
 * inactive one to switch column and reset to asc.
 */
export function toggleSort<TColumn extends string>(
  next: TColumn,
  current: TColumn,
  setColumn: (c: TColumn) => void,
  currentDir: 'asc' | 'desc',
  setDir: (d: 'asc' | 'desc') => void,
): void {
  if (next === current) {
    setDir(currentDir === 'asc' ? 'desc' : 'asc');
  } else {
    setColumn(next);
    setDir('asc');
  }
}
